import { useCallback, useState, useEffect, FC, useRef } from 'react';
import * as L from '@/components/Instruments/InstrumentsMainPageStyles';
import { SpinnerSize, Spinner, Separator, SearchBox, DefaultButton, Icon } from '@fluentui/react';
import FundService from '@/services/FundService';
import Filters from '@/components/Instruments/Filters';
import FeaturedManagers from '@/components/FeaturedManagers/FeaturedManagers';
import Auth from '@/Auth/Auth';
import groupFundsByFamily from '@/utils/groupFundsByFamily';
import { Link } from "react-router-dom";
import { ai } from '@/services/TelemetryService';
import { useHistory } from "react-router-dom";
import { translate } from "@/utils/i18n";
import { themes } from '@/components/ThemeContext';
import React from 'react';
import SearchFundsPrincipalTable, { Fund } from '@/components/tables/buscadorFondosTable/SearchFundsPrincipalTable';
import { Pagination } from '@/components/Pagination';
import { IPersistedFilters, IFilters, IFundGroup, IFundResponse, ILocationState, IInstrumentsProps, IColumnEditorSettings } from '@/components/Instruments/Interfaces/InstrumentsInterfaces';
import ColumnEditorModal from '@/components/Instruments/ColumnEditorModal';
import { loadColumnEditorSettings, saveColumnEditorSettings } from '@/components/Instruments/ColumnEditorStorage';

const FILTERS_STORAGE_KEY = 'searchFundsFilters';

const loadFiltersFromStorage = (): Partial<IPersistedFilters> =>
{
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);

    if(stored)
    {
        const parsed = JSON.parse(stored);

        // Convertimos null de vuelta a undefined para mantener coherencia
        const result: Partial<IPersistedFilters> = {};

        if(parsed.available !== null && parsed.available !== undefined)
        {
            result.available = parsed.available;
        }
        else if(parsed.hasOwnProperty('available'))
        {
            result.available = undefined;
        }

        if(parsed.income !== null && parsed.income !== undefined)
        {
            result.income = parsed.income;
        }
        else if(parsed.hasOwnProperty('income'))
        {
            result.income = undefined;
        }

        if(parsed.switchable !== null && parsed.switchable !== undefined)
        {
            result.switchable = parsed.switchable;
        }
        else if(parsed.hasOwnProperty('switchable'))
        {
            result.switchable = undefined;
        }

        if(parsed.indexed !== null && parsed.indexed !== undefined)
        {
            result.indexed = parsed.indexed;
        }
        else if(parsed.hasOwnProperty('indexed'))
        {
            result.indexed = undefined;
        }

        if(parsed.classCategory !== null && parsed.classCategory !== undefined)
        {
            result.classCategory = parsed.classCategory;
        }
        else if(parsed.hasOwnProperty('classCategory'))
        {
            result.classCategory = undefined;
        }

        return result;
    }
    return {};
};

const saveFiltersToStorage = (filters: IPersistedFilters): void =>
{
    // Convertimos undefined a null para que JSON.stringify los preserve
    const filtersToStore =
    {
        available: filters.available === undefined ? null : filters.available,
        income: filters.income === undefined ? null : filters.income,
        switchable: filters.switchable === undefined ? null : filters.switchable,
        indexed: filters.indexed === undefined ? null : filters.indexed,
        classCategory: filters.classCategory === undefined ? null : filters.classCategory
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToStore));
};

const getManagerFromQuery = (): string | null => new URLSearchParams(window.location.search).get("manager");

let lastPromise: Promise<IFundResponse> | null = null;

const InstrumentsMainPage: FC<IInstrumentsProps> = ({defaultFilters = {}, location, publicVersion, fromEF = false}) =>
{
    const localTheme = window.localStorage.getItem("theme") || "light";
    const theme = {palette: localTheme === "dark" ? themes.dark : themes.light};
    
    // Usar lazy initialization para que solo se ejecute una vez al montar el componente
    const [filters, setFilters] = useState<IFilters>(() =>
    {     
        const savedFilters = loadFiltersFromStorage();
        
        const defaultFilterValues: IFilters =
        {
            subcategory: [],
            manager: [],
            currency: [],
            available: true,          // SÍ por defecto
            income: undefined,        // SIN MARCAR por defecto
            switchable: undefined,    // SIN MARCAR por defecto
            indexed: undefined,       // SIN MARCAR por defecto
            class: undefined,
            classCategory: true,      // SÍ por defecto
            ...defaultFilters,
            ...location?.state
        };

        const managerFromQuery = getManagerFromQuery();
        if(managerFromQuery !== null && !defaultFilterValues.manager.includes(managerFromQuery))
        {
            defaultFilterValues.manager = [...defaultFilterValues.manager, managerFromQuery];
        }

        // Verificamos si hay filtros guardados (incluyendo propiedades con valor undefined)
        const hasStoredFilters = savedFilters &&
        (
            savedFilters.hasOwnProperty('available') ||
            savedFilters.hasOwnProperty('income') ||
            savedFilters.hasOwnProperty('switchable') ||
            savedFilters.hasOwnProperty('indexed') ||
            savedFilters.hasOwnProperty('classCategory')
        );

        // Si hay filtros guardados, aplicamos solo a los de ThreeWayToggle
        if(hasStoredFilters)
        {
            const initialFilters =
            {
                ...defaultFilterValues,
                available: savedFilters.hasOwnProperty('available') ? savedFilters.available : defaultFilterValues.available,
                income: savedFilters.hasOwnProperty('income') ? savedFilters.income : defaultFilterValues.income,
                switchable: savedFilters.hasOwnProperty('switchable') ? savedFilters.switchable : defaultFilterValues.switchable,
                indexed: savedFilters.hasOwnProperty('indexed') ? savedFilters.indexed : defaultFilterValues.indexed,
                classCategory: savedFilters.hasOwnProperty('classCategory') ? savedFilters.classCategory : defaultFilterValues.classCategory,
            };

            return initialFilters;
        }

        return defaultFilterValues;
    });
    
    const isInitialMount = useRef(true);
    
    let history = useHistory();
    //Para las redirecciones con término de búsqueda incluidas
    const searchTerm = (history?.location?.state as ILocationState)?.searchTerm ?? "";
    const [updateSearch, setUpdateSearch] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [recordsTotal, setRecordsTotal] = useState<number>(0);
    const [familyRecordsTotal, setFamilyRecordsTotal] = useState<number>(0);

    const [tableSearchTerm, setTableSearchTerm] = useState<string>(searchTerm);
    const [loading, setLoading] = useState<boolean>(false);
    const [fundGroups, setFundGroups] = useState<IFundGroup[]>([]);
    const [columnEditorSettings, setColumnEditorSettings] = useState<IColumnEditorSettings>(() => loadColumnEditorSettings());
    const [productsPerPage, setProductsPerPage] = useState<number>(() =>
    {
        const saved = columnEditorSettings.resultsPerPage;
        return saved === 0 ? 999999 : (saved || Number(Auth.getQtyPage()) || 10);
    });
    const [isManagerQueryPresent] = useState<boolean>(() => getManagerFromQuery() !== null);
    const [isColumnEditorOpen, setIsColumnEditorOpen] = useState<boolean>(false);
    const appInsights = ai.reactPlugin;

    const handleSaveColumnEditor = (newSettings: IColumnEditorSettings) =>
    {
        saveColumnEditorSettings(newSettings);
        setColumnEditorSettings(newSettings);
        if(newSettings.resultsPerPage !== productsPerPage)
        {
            setProductsPerPage(newSettings.resultsPerPage === 0 ? 999999 : newSettings.resultsPerPage);
        }
    };

    // Guardar filtros en localStorage cuando cambien (solo los ThreeWayToggle)
    // No guardamos en la primera carga para evitar sobrescribir los filtros guardados
    useEffect(() =>
    {
        if(!isInitialMount.current)
        {
            const filtersToSave: IPersistedFilters =
            {
                available: filters.available,
                income: filters.income,
                switchable: filters.switchable,
                indexed: filters.indexed,
                classCategory: filters.classCategory
            };
            saveFiltersToStorage(filtersToSave);
        }
    }, [filters.available, filters.income, filters.switchable, filters.indexed, filters.classCategory]);

    useEffect(() =>
    {
        const managerFromQuery = getManagerFromQuery();
        if(managerFromQuery !== null)
        {
            appInsights.trackEvent({name: "QuickLink", properties: { managerName: translate(managerFromQuery)}});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        if(isInitialMount.current)
        {
            return;
        }
        setTableSearchTerm((history?.location?.state as ILocationState)?.searchTerm ?? "");
        setUpdateSearch(c => !c);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, history?.location?.state]);

    const searchFunds = (): void =>
    {
        let {subcategory, manager, currency, available, income, switchable, indexed, classCategory} = filters;
        let subcategories = subcategory.map((sc) =>
        {
            return sc.morningStarCategoryId;
        });

        let promise = lastPromise = FundService.getFunds(page, productsPerPage, tableSearchTerm.toUpperCase(), subcategories, available, income, switchable, indexed, manager.length > 0 ? manager : undefined, currency.length > 0 ? currency : undefined, classCategory);

        promise.then((response: IFundResponse) =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
            setFamilyRecordsTotal(response?.total_Family ?? 0);
            setRecordsTotal(response.total);
            setFundGroups(groupFundsByFamily(response.results));
            setLoading(false);
        })
        .catch((error: any) =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
        })
        .finally(() =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
        });
    };

    const handleKey = (e: React.KeyboardEvent<HTMLInputElement>): void =>
    {
        if(e.key === 'Enter')
        {
            load();
        }
    };

    const load = (): void =>
    {
        setFamilyRecordsTotal(0);
        setRecordsTotal(0);
        setLoading(true);
        setFundGroups([]);
        setTimeout(() => searchFunds());
    };

    useEffect(() =>
    {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, productsPerPage, updateSearch]);

    // Búsqueda automática cuando cambien los filtros
    useEffect(() =>
    {
        if(!isInitialMount.current)
        {
            const timeoutId = setTimeout(() =>
            {
                // Fuera de la primera página la búsqueda la lanza el efecto de paginación al cambiar page
                if(page !== 0)
                {
                    setPage(0);
                }
                else
                {
                    load();
                }
            }, 300); // Debounce de 300ms

            return () => clearTimeout(timeoutId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.subcategory, filters.manager, filters.currency, filters.available, filters.income, filters.switchable, filters.indexed, filters.classCategory]);

    // Debe declararse después del resto de efectos para que todos vean el montaje inicial
    useEffect(() =>
    {
        isInitialMount.current = false;
    }, []);

    const handleChange = useCallback((value: any, filterId: keyof IFilters): void =>
    {
        setFilters(currentFilters => ({...currentFilters, [filterId]: value}));
    }, []);

    const handleClick = (managerNames: string | string[]): boolean | void =>
    {
        if(publicVersion && isManagerQueryPresent)
        {
            return false;
        }

        const managerNamesArray = Array.isArray(managerNames) ? managerNames : [managerNames];
        const alreadySelected = filters.manager.some((name) => managerNamesArray.includes(name));
        const filteredManagers = filters.manager.filter((name) => !managerNamesArray.includes(name));

        if(alreadySelected)
        {
            setFilters(
            {
                ...filters,
                manager: [...filteredManagers],
            });
        }
        else
        {
            appInsights.trackEvent({name: "QuickLink", properties: { managerName: translate(managerNamesArray[0])}});

            setFilters(
            {
                ...filters,
                manager: [...filteredManagers, ...managerNamesArray],
            });
        }
    };

    return(
        <L.Wrapper>
            <L.HeaderContainer>
                <L.TitleContainer>
                    <L.Title>Buscador de fondos</L.Title>
                </L.TitleContainer>
                {publicVersion && !isManagerQueryPresent &&
                (
                    <L.ButtonContainer>
                        <Link to="/">
                            <L.ButtonRegister>
                                Regístrate para operar
                            </L.ButtonRegister>
                        </Link>
                    </L.ButtonContainer>
                )}
            </L.HeaderContainer>
            <Separator />
            <L.FMContainer>
                <L.FMTitleContainer>
                    <L.FMTitle>GESTORAS DESTACADAS</L.FMTitle>
                </L.FMTitleContainer>
                <L.FMCardsContainer>
                    <FeaturedManagers
                        selectedManagers={filters.manager}
                        onClick={handleClick}
                        readOnly={publicVersion && isManagerQueryPresent}
                    />
                </L.FMCardsContainer>
            </L.FMContainer>
            <L.FiltersWrapper>
                <Filters onChange={handleChange} selectedFilters={filters} hiddenFilters={publicVersion && isManagerQueryPresent ? {manager: true} : {}} />
            </L.FiltersWrapper>
            <L.TableWrapper theme={theme}>
                <L.TableWidthManager theme={theme}>
                    <L.TableFilterContainer>
                        <L.TRCountContainer>
                            <L.TRTitle theme={theme}>
                                Resultados de la búsqueda
                                <L.TRCount theme={theme}>{`(${familyRecordsTotal})`}</L.TRCount>
                            </L.TRTitle>
                        </L.TRCountContainer>
                        <L.InputSearchContainer theme={theme}>
                            <SearchBox onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => handleKey(e)}
                                placeholder="Buscar fondos..."
                                value={tableSearchTerm}
                                autoComplete="off"
                                onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) =>
                                {
                                    let text = newValue || event?.target.value || "";
                                    setTableSearchTerm(text);
                                }}
                            />
                        </L.InputSearchContainer>
                        <L.EditColumnContainer>
                            <DefaultButton text="Buscar" onClick={load} />
                            <L.EditColumnsButton type="button" onClick={() => setIsColumnEditorOpen(true)}>
                                Editar columnas
                                <Icon iconName="TripleColumnEdit" style={{fontSize: 14, marginLeft: 6, color: 'inherit'}} />
                            </L.EditColumnsButton>
                        </L.EditColumnContainer>
                    </L.TableFilterContainer>
                    {loading ?
                    (
                        <Spinner size={SpinnerSize.large} />
                    ) :
                    (
                        <React.Fragment>
                            <L.TableContainer theme={theme}>
                                <SearchFundsPrincipalTable theme={theme}
                                    publicVersion={publicVersion || false}
                                    groups={fundGroups as Fund[]}
                                    columns={["Nombre", "Gestora", "Categoría", "Puntos IronIA"]}
                                    history={history}
                                    fromEF={fromEF}
                                    columnSettings={columnEditorSettings}
                                />
                            </L.TableContainer>
                            <Pagination
                                recordsTotal={recordsTotal}
                                currentPage={page}
                                itemsPerPage={productsPerPage}
                                onChangePage={(newPage: number) => {setPage(newPage);}}
                            />
                        </React.Fragment>
                    )}
                </L.TableWidthManager>
            </L.TableWrapper>
            <ColumnEditorModal isOpen={isColumnEditorOpen} onClose={() => setIsColumnEditorOpen(false)} settings={columnEditorSettings} onSave={handleSaveColumnEditor} />
        </L.Wrapper>
    );
};

export default InstrumentsMainPage;